import { useState } from "react";
import { Bot, CheckCircle2, Folder } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import {
  PILL_AVATAR_SIZE,
  PILL_GLYPH_SIZE,
  PreviewRow,
} from "~/pages/manifold-nextgen/components/PreviewRow";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

interface ProjectSetupWizardProps {
  onContinue: () => void;
}

export const ProjectSetupWizard = ({ onContinue }: ProjectSetupWizardProps) => {
  const { projectId, attachedAgents, attachedDatasets, isOwner } = useProjectContext();
  const [agentsDialogOpen, setAgentsDialogOpen] = useState(false);
  const [datasetsDialogOpen, setDatasetsDialogOpen] = useState(false);

  const agents = attachedAgents ?? [];
  const datasets = attachedDatasets ?? [];
  const hasAgents = agents.length > 0;
  const hasDatasets = datasets.length > 0;

  // A non-owner can't add agents or datasets, so the setup actions are useless
  // to them — show a hand-off message instead of dead buttons.
  if (!isOwner) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold">This project isn&apos;t set up yet</h1>
          <p className="text-sm text-muted-foreground">
            Ask a project owner to add an agent before you can start a session.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Get set up for your new session</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              To start a session, add an agent to the project. Datasets are optional.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <SetupCard
              icon={<Bot className="size-5" />}
              title="Add Agents"
              description="Choose one or more AI agents to use in this project"
              complete={hasAgents}
              onClick={() => setAgentsDialogOpen(true)}
            >
              {hasAgents && (
                <PreviewRow
                  className="pl-14"
                  maxPreview={5}
                  items={agents.map((a) => ({
                    key: a.id,
                    label: a.display_name,
                    leading: <AgentAvatar agent={a} size={PILL_AVATAR_SIZE} />,
                  }))}
                />
              )}
            </SetupCard>

            <SetupCard
              icon={<Folder className="size-5" />}
              title="Add Datasets"
              description="Optionally connect datasets for your agents to work with"
              complete={hasDatasets}
              onClick={() => setDatasetsDialogOpen(true)}
            >
              {hasDatasets && (
                <PreviewRow
                  className="pl-14"
                  maxPreview={5}
                  items={datasets.map((d) => ({
                    key: d.id,
                    label: d.title,
                    leading: <DatasetIcon dataset={d} size={PILL_GLYPH_SIZE} />,
                  }))}
                />
              )}
            </SetupCard>
          </div>

          <Button className="self-center px-8" disabled={!hasAgents} onClick={onContinue}>
            Start Session
          </Button>
        </div>
      </div>

      <ProjectAgentsDialog
        projectId={projectId}
        open={agentsDialogOpen}
        onOpenChange={setAgentsDialogOpen}
        attached={agents}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={datasetsDialogOpen}
        onOpenChange={setDatasetsDialogOpen}
        attachedIds={datasets.map((d) => d.id)}
      />
    </>
  );
};

interface SetupCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  complete: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

const SetupCard = ({ icon, title, description, complete, onClick, children }: SetupCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex w-full cursor-pointer flex-col gap-3 rounded-lg border px-5 py-4 text-left transition-colors hover:bg-accent/50",
      complete ? "border-green-500/40 bg-green-50/50 dark:bg-green-950/20" : "border-border"
    )}
  >
    <div className="flex w-full items-center gap-4">
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          complete
            ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
            : "bg-muted text-muted-foreground"
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {complete ? (
        <CheckCircle2 className="size-6 shrink-0 self-center text-green-600 dark:text-green-400" />
      ) : (
        <span className="shrink-0 text-xs font-medium text-primary">Add →</span>
      )}
    </div>
    {children}
  </button>
);
